import { Alerta, BarraProgreso, Badge, Button, Galeria, idPasoRecorrido, Row, Spinner, Stack } from "@/components";
import type { ItemRecorrido, Novedad, PasoRecorrido } from "@/lib/api";
import { CLASE_TAREA, estadoItem, estadoPaso, fotosGaleria, textoTareasPaso } from "@/lib/avance";
import { cn } from "@/lib/cn";
import { formatFecha } from "@/lib/format";

const PUNTO = { terminada: "bg-exito text-texto-invertido", en_curso: "bg-primario text-texto-invertido", sin_empezar: "border-2 border-borde-fuerte bg-superficie" };
const ICONO = { terminada: "✓", en_curso: "●", sin_empezar: "" };
const SIN_DESCRIPCION: Record<PasoRecorrido["tipo"], string | null> = {
  terreno: null,
  rubro: null,
  grupo: null,
  hito: "Un momento clave de la obra: se cumple cuando termina todo lo que lo antecede.",
  fin: null,
};

/** Sólo admin, en los sub-hitos: marcarlo como cumplido (publica la novedad) o volverlo a pendiente. */
export interface AccionesSubHito {
  onCumplir: (item: ItemRecorrido) => void;
  onDesmarcar: (item: ItemRecorrido) => void;
  /** Recorrido del desarrollo: marcar o sacar "No corresponde" (cuenta como hecho, sin novedad). */
  onNoCorresponde?: (item: ItemRecorrido, valor: boolean) => void;
}

function Tarea({ item, acciones }: { item: ItemRecorrido; acciones?: AccionesSubHito }) {
  const estado = estadoItem(item);
  const conAcciones = acciones && item.tipo === "subhito";
  return (
    <li className="flex gap-3 py-3 first:pt-0 last:pb-0">
      <span aria-hidden className={cn("mt-0.5 grid size-5 shrink-0 place-items-center rounded-full text-[0.625rem]", PUNTO[item.estado])}>
        {ICONO[item.estado]}
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        {item.clase && (
          <span className="pb-0.5">
            <Badge tono={CLASE_TAREA[item.clase].tono}>{CLASE_TAREA[item.clase].texto}</Badge>
          </span>
        )}
        <span className={cn("font-medium", item.estado === "sin_empezar" && "text-texto-suave", item.no_corresponde && "line-through")}>{item.nombre}</span>
        {item.descripcion && <span className="text-chico text-texto-suave">{item.descripcion}</span>}
        <span className={cn("text-chico", item.estado === "terminada" ? "text-exito" : item.estado === "en_curso" ? "text-primario" : "text-texto-suave")}>{estado.texto}</span>
        {conAcciones && !item.automatico && (
          <Row envolver espacio={2} className="pt-1">
            {item.no_corresponde ? (
              acciones.onNoCorresponde && (
                <Button variante="fantasma" tamanio="sm" aria-label={`Sí corresponde: ${item.nombre}`} onClick={() => acciones.onNoCorresponde?.(item, false)}>
                  Sí corresponde
                </Button>
              )
            ) : item.estado === "terminada" ? (
              <Button variante="fantasma" tamanio="sm" aria-label={`Desmarcar ${item.nombre}`} onClick={() => acciones.onDesmarcar(item)}>
                Desmarcar
              </Button>
            ) : (
              <>
                <Button variante="secundario" tamanio="sm" aria-label={`Marcar cumplido: ${item.nombre}`} onClick={() => acciones.onCumplir(item)}>
                  Marcar cumplido
                </Button>
                {acciones.onNoCorresponde && item.clase && (
                  <Button variante="fantasma" tamanio="sm" aria-label={`No corresponde: ${item.nombre}`} onClick={() => acciones.onNoCorresponde?.(item, true)}>
                    No corresponde
                  </Button>
                )}
              </>
            )}
          </Row>
        )}
      </div>
    </li>
  );
}

function NovedadCorta({ fid, novedad }: { fid: number; novedad: Novedad }) {
  return (
    <li className="flex flex-col gap-2 rounded-control bg-superficie-2 p-3">
      <span className="text-chico text-texto-suave">{formatFecha(novedad.fecha)}</span>
      <span className="font-medium">{novedad.titulo}</span>
      {novedad.texto && <p className="line-clamp-3 whitespace-pre-line text-chico text-texto-suave">{novedad.texto}</p>}
      <Galeria etiqueta={`Fotos de ${novedad.titulo}`} fotos={fotosGaleria(fid, novedad)} maximo={3} />
    </li>
  );
}

export interface DetallePasoProps {
  id: string;
  fid: number;
  paso: PasoRecorrido;
  numero: number;
  total: number;
  novedades: Novedad[] | null; // null = el paso no tiene novedades propias
  cargandoNovedades: boolean;
  errorNovedades: string | null;
  onAnterior: (() => void) | null;
  onSiguiente: (() => void) | null;
  acciones?: AccionesSubHito;
}

/** El panel del paso elegido en el recorrido: qué es, en qué estado está, qué incluye y sus últimas novedades. */
export function DetallePaso({ id, fid, paso, numero, total, novedades, cargandoNovedades, errorNovedades, onAnterior, onSiguiente, acciones }: DetallePasoProps) {
  const estado = estadoPaso(paso);
  const descripcion = paso.descripcion ?? SIN_DESCRIPCION[paso.tipo];
  return (
    <section id={id} role="tabpanel" aria-labelledby={idPasoRecorrido(paso.clave)} className="flex flex-col gap-5 border-t border-borde pt-5">
      <Stack espacio={2}>
        <Row envolver espacio={2} alinear="centro">
          <Badge tono={estado.tono}>{estado.texto}</Badge>
          <span className="text-chico text-texto-suave">{paso.fase ? `${paso.fase} · Paso ${numero} de ${total}` : `Paso ${numero} de ${total}`}</span>
        </Row>
        <h3 className="text-titulo font-light tracking-tight">{paso.nombre}</h3>
        {descripcion && <p className="text-texto-suave">{descripcion}</p>}
        {paso.responsables && (
          <p className="text-chico">
            <span className="text-texto-suave">Responsables: </span>
            {paso.responsables}
          </p>
        )}
      </Stack>

      {(paso.tipo === "rubro" || paso.tipo === "grupo") && (
        <Stack espacio={2}>
          <Row justificar="entre" alinear="fin">
            <span className="text-chico text-texto-suave">{textoTareasPaso(paso)}</span>
            <span className="text-subtitulo font-semibold tabular-nums">{`${paso.porcentaje ?? 0} %`}</span>
          </Row>
          <BarraProgreso porcentaje={paso.porcentaje ?? 0} tono={paso.estado === "terminada" ? "exito" : "primario"} etiqueta={`Avance de ${paso.nombre}`} />
        </Stack>
      )}

      {paso.items.length > 0 && (
        <Stack espacio={3}>
          <h4 className="text-chico font-semibold uppercase tracking-wide text-texto-suave">Qué incluye</h4>
          <ul aria-label={`Qué incluye ${paso.nombre}`} className="flex flex-col divide-y divide-borde">
            {paso.items.map((i) => (
              <Tarea key={i.etapa_id ?? `hito-${i.hito_id}`} item={i} acciones={acciones} />
            ))}
          </ul>
        </Stack>
      )}

      {novedades !== null && (
        <Stack espacio={3}>
          <h4 className="text-chico font-semibold uppercase tracking-wide text-texto-suave">Novedades de esta etapa</h4>
          {cargandoNovedades && (
            <div className="grid place-items-center py-4 text-primario">
              <Spinner />
            </div>
          )}
          <Alerta texto={errorNovedades} />
          {!cargandoNovedades && !errorNovedades && novedades.length === 0 && (
            <p className="text-chico text-texto-suave">Todavía no hay novedades de esta etapa.</p>
          )}
          {novedades.length > 0 && (
            <ul aria-label={`Novedades de ${paso.nombre}`} className="flex flex-col gap-3">
              {novedades.map((n) => (
                <NovedadCorta key={n.id} fid={fid} novedad={n} />
              ))}
            </ul>
          )}
        </Stack>
      )}

      <Row justificar="entre" espacio={2}>
        <Button variante="secundario" tamanio="sm" disabled={!onAnterior} onClick={() => onAnterior?.()}>
          ← Anterior
        </Button>
        <Button variante="secundario" tamanio="sm" disabled={!onSiguiente} onClick={() => onSiguiente?.()}>
          Siguiente →
        </Button>
      </Row>
    </section>
  );
}
